import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "@assets/css/common/style_main.css";
import "@assets/css/common/style_table.css";
import "@assets/css/common/style_modal.css";
import "@assets/css/common/style_calendar.css";

// Custom hooks
import useTheme from "@hooks/useTheme";

// 공통 컴포넌트
import AlertModal from "@components/common/AlertModal";
import ConfirmModal from "@components/common/ConfirmModal";
import Calendar from "@components/common/Calendar.jsx";
import DesignThemeInputModal from "@pages/design-management/components/DesignThemeInputModal.jsx";
import PaginationZeroBased from "@components/common/PaginationZeroBased";

// 도메인 컴포넌트
import ThemeTable from "./components/ThemeTable";
import ThemeFilters from "./components/ThemeFilters";
import ThemeDetailModal from "./components/ThemeDetailModal";
import { useAuthStore } from "@/store/useAuthStore.jsx";
import moment from "moment";

const DesignManagement = () => {
    const navigate = useNavigate();

    // 테마 관련 커스텀 훅 사용
    const {
        themes,
        loading,
        error,
        branches,
        createTheme,
        updateTheme,
        deleteTheme,
    } = useTheme();

    // Zustand 스토어에서 직접 사용자 정보 가져오기
    const { user } = useAuthStore();

    // 체크박스 상태 관리
    const [selectAll, setSelectAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    // 모달 상태 관리
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [confirmMessage, setConfirmMessage] = useState("");
    const [confirmCallback, setConfirmCallback] = useState(null);
    const [currentTheme, setCurrentTheme] = useState(null);

    // 모달 입력값 상태 관리
    const [modalInputs, setModalInputs] = useState({
        userName : "",
        themeId: "",
        themeName: "",
        startDatetime: null,
        endDatetime: null,
        isActive: false,
        allBranches: branches || [],
        branches: [],
    });

    // 달력 관련 상태 관리
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({
        top: 0,
        left: 0,
    });

    // 상세정보 모달 상태 관리
    const [showDetailModal, setShowDetailModal] = useState(false);

    // 페이지네이션 상태 관리
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // 체크박스 전체 선택/해제 핸들러
    const handleSelectAll = (e) => {
        setSelectAll(e.target.checked);
        // 모든 테마 ID 또는 빈 배열로 설정
        setSelectedItems(
            e.target.checked ? themes.map((theme) => theme.id) : []
        );
    };

    // 개별 체크박스 핸들러
    const handleItemSelect = (id, e) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }

        // 전체 선택 상태 업데이트
        if (e.target.checked && selectedItems.length + 1 === themes.length) {
            setSelectAll(true);
        } else if (!e.target.checked && selectAll) {
            setSelectAll(false);
        }
    };

    // 테마 추가 버튼 핸들러
    const handleAddThemeClick = () => {
        // 입력값 초기화
        setModalInputs({
            userName : user.name || "",
            themeId: "",
            themeName: "",
            startDatetime: null,
            endDatetime: null,
            isActive: false,
            allBranches: branches || [],
            branches: [],
        });
        setShowAddModal(true);
    };

    // 편집 버튼 핸들러
    const handleEditClick = (theme) => {
        console.log('=== handleEditClick 시작 (DesignManagement) ===');
        console.log('theme:', theme);
        console.log('theme.branches:', theme?.branches);
        console.log('branches (전체 지점 목록):', branches);

        setCurrentTheme(theme);

        const modalData = {
            userName : theme?.createdBy?.name || '',
            themeId: theme?.id,
            themeName: theme?.themeName,
            startDatetime: moment(theme?.startDatetime).format("YYYYMMDD") || null,
            endDatetime: moment(theme?.endDatetime).format("YYYYMMDD") || null,
            isActive: theme?.isActive || false,
            allBranches: branches || [],
            branches: theme?.branches || [],
        };

        console.log('modalInputs 설정값:', modalData);
        console.log('modalInputs.branches:', modalData.branches);
        console.log('modalInputs.allBranches:', modalData.allBranches);

        setModalInputs(modalData);

        setShowEditModal(true);
        console.log('=== handleEditClick 종료 ===');
    };

    // 삭제 버튼 핸들러
    const handleDeleteClick = (theme) => {
        setCurrentTheme(theme);
        setConfirmMessage(`${theme.themeName} 테마를 삭제하시겠습니까?`);
        setConfirmCallback(() => async () => {
            try {
                // 테마 삭제 API 호출
                await deleteTheme(theme.id);
                setAlertMessage("삭제되었습니다.");
                setShowAlertModal(true);
            } catch (error) {
                setAlertMessage("삭제 중 오류가 발생했습니다.");
                setShowAlertModal(true);
            }
        });
        setShowConfirmModal(true);
    };

    // 모달 닫기 핸들러
    const handleCloseModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowConfirmModal(false);
        setShowAlertModal(false);
        setShowCalendar(false);
    };

    // 알림 모달만 닫는 함수
    const handleCloseAlertModal = () => {
        setShowAlertModal(false);
    };

    // 확인 모달만 닫는 함수
    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
    };

    // 중복 확인 핸들러
    const handleCheckDuplicate = () => {
        const themeName = modalInputs.themeName.trim();
        if (!themeName) {
            setAlertMessage("테마명을 입력해주세요.");
            setShowAlertModal(true);
            return;
        }

        // 기존 테마명 목록
        const existingThemes = themes.map((theme) => theme.themeName);

        // 수정 모드에서는 현재 편집 중인 테마명은 제외
        if (
            showEditModal &&
            currentTheme &&
            themeName === currentTheme.themeName
        ) {
            setAlertMessage("사용 가능한 테마명입니다.");
            setShowAlertModal(true);
            return;
        }

        if (existingThemes.includes(themeName)) {
            setAlertMessage(
                "이미 사용 중인 테마명입니다. 다른 테마명을 입력해주세요."
            );
            setShowAlertModal(true);
        } else {
            setAlertMessage("사용 가능한 테마명입니다.");
            setShowAlertModal(true);
        }
    };

    // 테마 추가/수정 확인 핸들러
    const handleConfirm = async () => {
        // 입력값 검증
        if (!modalInputs.themeName) {
            setAlertMessage("테마명을 입력해주세요.");
            setShowAlertModal(true);
            return;
        }

        if (!modalInputs.startDatetime || !modalInputs.endDatetime) {
            setAlertMessage("사용 기간을 선택해주세요.");
            setShowAlertModal(true);
            return;
        }

        try {
            if (showAddModal) {
                // branchIds 생성 로그
                console.log('=== 테마 추가 - branchIds 생성 ===');
                console.log('modalInputs.branches:', modalInputs.branches);
                const branchIds = modalInputs.branches.map((branch) => {
                    const branchId = branch.branchId !== undefined && branch.branchId !== null ? branch.branchId : branch.id;
                    console.log(`branch.id=${branch.id}, branch.branchId=${branch.branchId} → branchId=${branchId}`);
                    return branchId;
                });
                console.log('최종 branchIds:', branchIds);

                // 테마 추가 API 호출
                const responseData = await createTheme({
                    themeName: modalInputs.themeName,
                    startDatetime: modalInputs.startDatetime,
                    endDatetime: modalInputs.endDatetime,
                    branchIds: branchIds,
                    isActive: modalInputs.isActive,
                });

                const themeId = responseData.id;

                setShowAddModal(false);

                // 확인 모달 표시
                setConfirmMessage("디자인을 진행하시겠습니까?");
                setConfirmCallback(() => () => {
                    // PageDesign.jsx로 이동
                    navigate(
                        `/design-management/page-design?page=start&theme=${modalInputs.themeName}&author=${modalInputs.author}&date=${modalInputs.startDatetime}&themeId=${themeId}`
                    );
                });
                setShowConfirmModal(true);
            } else if (showEditModal && currentTheme) {
                // branchIds 생성 로그
                console.log('=== 테마 수정 - branchIds 생성 ===');
                console.log('modalInputs.branches:', modalInputs.branches);
                const branchIds = modalInputs.branches.map((branch) => {
                    const branchId = branch.branchId !== undefined && branch.branchId !== null ? branch.branchId : branch.id;
                    console.log(`branch.id=${branch.id}, branch.branchId=${branch.branchId}, branch.branchName=${branch.branchName} → branchId=${branchId}`);
                    return branchId;
                });
                console.log('최종 branchIds:', branchIds);

                // 테마 수정 API 호출
                await updateTheme(currentTheme.id, {
                    themeName: modalInputs.themeName,
                    startDatetime: modalInputs.startDatetime,
                    endDatetime: modalInputs.endDatetime,
                    branchIds: branchIds,
                    isActive: modalInputs.isActive,
                });

                setAlertMessage("테마 정보가 수정되었습니다.");
                setShowEditModal(false);
                setShowAlertModal(true);
            }
        } catch (error) {
            console.error(error);
            setAlertMessage("처리 중 오류가 발생했습니다.");
            setShowAlertModal(true);
        }
    };

    // 디자인 수정 페이지로 이동 핸들러
    const handleDesignEditClick = () => {
        // 현재 테마와 수정된 테마 정보 비교
        if (currentTheme) {
            const [origStartDatetime, origEndDatetime] =
                currentTheme.usagePeriod.split(" - ");
            const isChanged =
                currentTheme.themeName !== modalInputs.themeName ||
                origStartDatetime !== modalInputs.startDatetime ||
                origEndDatetime !== modalInputs.endDatetime;

            if (isChanged) {
                // 변경사항이 있을 경우 확인 모달 표시
                setConfirmMessage("변경사항이 있어요. 저장할까요?");
                setConfirmCallback(() => () => {
                    // 저장 로직 실행
                    handleConfirm();
                    // 페이지 이동 로직
                    setShowConfirmModal(false);
                    setShowEditModal(false);
                    navigate(
                        `/design-management/page-design?page=start&theme=${currentTheme.themeName}&author=${modalInputs.author}&date=${modalInputs.startDatetime}&themeId=${modalInputs.themeId}`
                    );
                });
                setShowConfirmModal(true);
            } else {
                // 변경사항이 없으면 바로 이동
                setShowEditModal(false);
                navigate(
                    `/design-management/page-design?page=start&theme=${currentTheme.themeName}&author=${modalInputs.author}&date=${modalInputs.startDatetime}&themeId=${modalInputs.themeId}`
                );
            }
        }
    };

    // 확인 모달 확인 버튼 핸들러
    const handleConfirmModalConfirm = () => {
        if (confirmCallback) {
            confirmCallback();
        }
        setShowConfirmModal(false);
    };

    // 날짜 선택 핸들러 수정
    const handleDateSelect = (startDatetime, endDatetime) => {
        // 시작일과 종료일을 함께 업데이트
        setModalInputs((prev) => ({
            ...prev,
            startDatetime: moment(startDatetime).format("YYYYMMDD"),
            endDatetime: moment(endDatetime).format("YYYYMMDD"),
        }));
        setShowCalendar(false);
    };

    // 일괄 삭제 핸들러
    const handleBulkDelete = () => {
        if (selectedItems.length === 0) return;

        setConfirmMessage(
            `선택한 ${selectedItems.length}개의 테마를 삭제하시겠습니까?`
        );
        setConfirmCallback(() => async () => {
            try {
                // 선택된 모든 테마 삭제
                const deletePromises = selectedItems.map((id) =>
                    deleteTheme(id)
                );
                await Promise.all(deletePromises);

                setSelectedItems([]);
                setSelectAll(false);
                setAlertMessage("선택한 테마가 삭제되었습니다.");
                setShowAlertModal(true);
            } catch (error) {
                setAlertMessage("삭제 중 오류가 발생했습니다.");
                setShowAlertModal(true);
            }
        });
        setShowConfirmModal(true);
    };

    // 지점 추가 핸들러
    const handleAddBranch = (branch) => {

    };

    // 지점 제거 핸들러
    const handleRemoveBranch = (branch) => {

    };

    // 상세정보 모달 닫기 핸들러
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
    };

    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (theme) => {
        setCurrentTheme(theme);
        setShowDetailModal(true);
    };

    // 페이지 변경 핸들러
    const handlePageChange = (newPage) => {
        setPage(newPage);
        // 실제 구현에서는 여기서 API 호출하여 새 페이지 데이터를 가져옴
    };

    return (
        <>
            <div className="page-title-container">
                <h1 className="page-title">페이지 디자인 테마</h1>
                <div className="page-title-icon">
                    <a
                        href="#"
                        id="add-theme-btn"
                        title="페이지 디자인 테마 추가"
                        onClick={handleAddThemeClick}
                    >
                        <i className="ri-add-circle-line"></i>
                    </a>
                </div>
            </div>

            <div className="page-container">
                {/* 로딩 표시 */}
                {loading && <div className="loading">로딩 중...</div>}

                {/* 에러 표시 */}
                {error && <div className="error-message">{error}</div>}

                {/* 테마 테이블 */}
                <ThemeTable
                    themes={themes}
                    selectAll={selectAll}
                    selectedItems={selectedItems}
                    onSelectAll={handleSelectAll}
                    onItemSelect={handleItemSelect}
                    onInfoClick={handleInfoClick}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                />

                {/* 페이지네이션 */}
                <PaginationZeroBased
                    page={page}
                    totalPages={totalPages}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* 테마 추가 모달 */}
            {showAddModal && (
                <DesignThemeInputModal
                    title="페이지 디자인 테마 추가"
                    inputs={modalInputs}
                    setInputs={setModalInputs}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    onCheckDuplicate={handleCheckDuplicate}
                    onAddBranch={handleAddBranch}
                    onRemoveBranch={handleRemoveBranch}
                    showToggle={true}
                    toggleLabel="활성화 여부"
                    toggleName="isActive"
                    showSettingButton={false}
                    itemNameLabel="테마명"
                    itemNamePlaceholder="테마명을 입력하세요"
                    itemNameField="themeName"
                    isEdit={false}
                />
            )}

            {/* 테마 수정 모달 */}
            {showEditModal && currentTheme && (
                <DesignThemeInputModal
                    title="페이지 디자인 테마 수정"
                    inputs={modalInputs}
                    setInputs={setModalInputs}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    onCheckDuplicate={handleCheckDuplicate}
                    onAddBranch={handleAddBranch}
                    onRemoveBranch={handleRemoveBranch}
                    showToggle={true}
                    toggleLabel="활성화 여부"
                    toggleName="isActive"
                    showSettingButton={true}
                    settingButtonText="디자인 수정하기"
                    onSettingClick={handleDesignEditClick}
                    itemNameLabel="테마명"
                    itemNamePlaceholder="테마명을 입력하세요"
                    itemNameField="themeName"
                    isEdit={true}
                />
            )}

            {/* 알림 모달 */}
            <AlertModal
                isOpen={showAlertModal}
                message={alertMessage}
                onClose={handleCloseAlertModal}
            />

            {/* 확인 모달 */}
            <ConfirmModal
                isOpen={showConfirmModal}
                message={confirmMessage}
                onConfirm={handleConfirmModalConfirm}
                onCancel={handleCloseConfirmModal}
            />

            {/* 달력 컴포넌트 */}
            {showCalendar && (
                <Calendar
                    onDateSelect={handleDateSelect}
                    onClose={() => setShowCalendar(false)}
                    position={calendarPosition}
                />
            )}

            {/* 테마 상세정보 모달 */}
            <ThemeDetailModal
                isOpen={showDetailModal}
                theme={currentTheme}
                onClose={handleCloseDetailModal}
            />
        </>
    );
};

export default DesignManagement;
